import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../../components/SEO";

const stepsBorrower = [
  {
    number: "1",
    title: "Provide Business Information",
    description:
      "Provide entity’s basic details and a consent for KYC verification and financial data retrieval from third party portals. Minimal docs required.",
    img: "/howItWorks1.png",
  },
  {
    number: "2",
    title: "Get Credit Report",
    description:
      "Dequitec's AI credit tool provides borrowers a credit rating, one-pager, CMA, and financial projections (nominal fee). FIs receive this data with anonymized KYC to prevent spam.",
    img: "/howItWorks2.png",
  },
  {
    number: "3",
    title: "Get Indicative Offers from Banks & FIs",
    description:
      "View upfront indicative loan offers (amount, tenure, pricing, conditions) from FIs before applying.",
    img: "/howItWorks3.png",
  },
  {
    number: "4",
    title: "Choose the best offer/FI and apply for loan ",
    description:
      "Dequitec's web/app workflow system facilitates selection of the best indicative FI offer, application to up to 3 FIs (with commitment fees), and provides ongoing support and updates through final sanction.",
    img: "/howItWorks4.png",
  },
  {
    number: "5",
    title: "Loan Disbursement",
    description:
      "Accept final sanction from preferred lender for fast, secure, and hassle-free disbursement to your account.",
    img: "/howItWorks5.png",
  },
];

const stepsConsultant = [
  {
    number: "1",
    title: "Upload Your Client's Proposal",
    description:
      `● Log in to your consultant account.\n` +
      `● Navigate to the "Upload Proposal" section.\n` +
      `● Fill in all the required details about your client's business, including their requirements, financial history, and the specific loan amount.\n` +
      `● Attach necessary documents: financial statements, business plans, and any relevant information.\n` +
      `● Our system creates a comprehensive snapshot for lenders based on your uploaded data.\n` +
      `● The loan summary is shared with relevant lenders based on industry and loan type.\n` +
      `● Confidentiality Assured: All KYC/personal details are hidden from lenders at this stage.`,
    img: "/howItWorks1.png",
  },
  {
    number: "2",
    title: "Receive and Present Loan Offers",
    description:
      `● Lenders submit "indicative loan offers" through the platform with terms like interest rate, repayment plan, and collateral.\n` +
      `● You'll get notified immediately once any offer is received.\n` +
      `● Log in to your dashboard to review and compare all received offers.\n` +
      `● Discuss these offers with your client and help them choose the most suitable option.`,
    img: "/howItWorks3.png",
  },
  {
    number: "3",
    title: "Acceptance and Finalization",
    description:
      `● Once your client selects a loan offer, mark it on the platform by selecting the lender.\n` +
      `● The lender's contact details will be provided for final formalities and disbursement.\n` +
      `● Earn Referral Fees: Receive 75% of the lender's referral fee for successful loan closures.`,
    img: "/howItWorks5.png",
  },
];

const stepsLender = [
  {
    number: "1",
    title: "Receive Curated Loan Proposals",
    description:
      `● Our algorithm matches your lending criteria with the business requirements of borrowers and consultants.\n` +
      `● You will receive a notification whenever a new proposal that fits your profile is available.\n` +
      `● Log in to your lender dashboard to view the proposal briefs. Each brief includes a snapshot of the business's financial performance, a summary of their loan requirement, and a brief description of their business plan.\n` +
      `● Please Note: For the security and privacy of our users, all KYC details are concealed at this stage. You will only receive this information after your indicative offer has been accepted by the borrower.`,
    img: "/howItWorks1.png",
  },
  {
    number: "2",
    title: "Submit an Indicative Offer",
    description:
      `● If you are interested in a specific proposal, you can submit an "indicative loan offer" directly through the platform.\n` +
      `● Fill the predesigned indicative form in your dashboard, including key terms of the loan such as proposed interest rate, repayment period, and any specific conditions.`,
    img: "/howItWorks3.png",
  },
  {
    number: "3",
    title: "Await Borrower's Decision",
    description:
      `● Once you submit your offer, the borrower or their consultant will be notified.\n` +
      `● They will review your offer alongside others they may receive.\n` +
      `● Our platform keeps you informed of the status of your offer — whether accepted, rejected, or under consideration.`,
    img: "/howItWorks4.png",
  },
  {
    number: "4",
    title: "Finalize the Loan Agreement",
    description:
      `● If your offer is accepted, the proposal is logged with your institution.\n` +
      `● You will receive borrower or consultant contact details to proceed with due diligence and loan documentation.`,
    img: "/howItWorks5.png",
  },
];

const StepNumber = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  marginRight: "10px",
  fontSize: "1.5rem",
  fontWeight: "bold",
  color: "#18CB96",
  zIndex: 10,
}));

const HowItWorks = () => {
  const theme = useTheme();
  const location = useLocation();
  const currentPath = location.pathname.split("/").pop(); // Get the last part of the path
  // const activeSubTab = useSelector((state) => state.user.activeSubTab);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // Changed from 'sm' to 'md' to include tablets
  const [openIndex, setOpenIndex] = useState(null);
  const [openContent, setOpenContent] = useState(false);
  // console.log("Active Sub Tab:", location.pathname,location.href,currentPath); // Debugging line
  return (
    <>
      <SEO
        title="How Our Fast MSME & Business Loan Process Works | Dequitec"
        description="Learn how we simplify working capital loans, loan against property, and MSME funding with expert guidance, quick approvals, and tailored solutions."
        canonical="https://www.dequitec.com/how-it-works"
      />
      <Container maxWidth="lg" sx={{ py: 5, position: "relative", mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            marginTop: "1%",
          }}
        >
          <Box
            sx={{ width: "40px", height: "2px", backgroundColor: "#004AAD" }}
          />
          <Typography
            variant="h8"
            sx={{ fontWeight: "bold", color: "#004AAD" }}
          >
            How It Works -{" "}
            {currentPath.charAt(0).toUpperCase() + currentPath.slice(1)}
          </Typography>
        </Box>
        {/* Section 1 */}
        {currentPath !== "borrower" && (
          <Grid
            container
            spacing={8}
            alignItems="center"
            justifyContent={"center"}
          >
            <Grid item xs={12} md={6} lg={3}>
              <Paper elevation={0} sx={{ borderRadius: 2, padding: 4 }}>
                <img
                  src="/AboutUsBanner.webp"
                  alt="About Tec"
                  style={{
                    height: "22vh",
                    borderRadius: "12px",
                    objectFit: "contain",
                    width: "100%",
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              {currentPath === "consultant" ? (
                <Typography sx={{ mb: 1 }}>
                  Welcome to Dequitec ! As a loan consultant, you play a vital
                  role in connecting businesses with the financing they need.
                  Our platform streamlines the process, making it easier for you
                  to manage your clients' loan applications and secure the best
                  possible offers.
                </Typography>
              ) : (
                <Typography sx={{ mb: 1 }}>
                  Welcome to Dequitec !, where we connect you with a curated
                  stream of high-quality loan applications from businesses
                  seeking financing. Our system provides you with a clear and
                  concise snapshot of each proposal, allowing you to make
                  informed decisions and submit competitive offers.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
        {(currentPath === "borrower"
          ? stepsBorrower
          : currentPath === "consultant"
          ? stepsConsultant
          : stepsLender
        ).map((step, index) => (
          <Grid
            container
            spacing={3}
            key={index}
            alignItems="center"
            justifyContent="center"
            sx={{ position: "relative", py: 5 }}
            direction={isSmallScreen ? "column" : "row"}
          >
            {isSmallScreen && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 5,
                  mb: 2,
                }}
              >
                <img
                  src={step.img}
                  alt={step.title}
                  style={{
                    width: "70%",
                    maxWidth:
                      theme.breakpoints.values.sm > 0 ? "350px" : "250px",
                  }}
                />
              </Grid>
            )}

            {!isSmallScreen && index % 2 === 0 ? (
              <>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5, // Ensure text is in front of the dotted line
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <StepNumber>{step.number}</StepNumber>
                    <Typography variant="h5" color="#004AAD">
                      {step.title}
                    </Typography>
                  </Box>
                  {/* <Typography>{step.description}</Typography> */}
                  {/* Render description line-by-line */}
                  <>
                    {step.description.split("\n").map((line, idx) => (
                      <Typography key={idx} sx={{ mb: 1 }}>
                        {line.trim()}
                      </Typography>
                    ))}
                  </>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5, // Ensure image is above the dotted line
                  }}
                >
                  <img
                    src={step.img}
                    alt={step.title}
                    style={{ height: "50%", width: "50%" }}
                  />
                </Grid>
              </>
            ) : !isSmallScreen ? (
              <>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5, // Ensure image is above the dotted line
                  }}
                >
                  <img
                    src={step.img}
                    alt={step.title}
                    style={{ height: "50%", width: "50%" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 5, // Ensure text is above the dotted line
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <StepNumber>{step.number}</StepNumber>
                    <Typography variant="h5" color="#004AAD">
                      {step.title}
                    </Typography>
                  </Box>
                  <>
                    {step.description.split("\n").map((line, idx) => (
                      <Typography key={idx} sx={{ mb: 1 }}>
                        {line.trim()}
                      </Typography>
                    ))}
                  </>
                </Grid>
              </>
            ) : null}

            {isSmallScreen && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 5,
                  textAlign: "center",
                  px: { xs: 2, sm: 4 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <StepNumber>{step.number}</StepNumber>
                  <Typography variant="h5" color="#004AAD">
                    {step.title}
                  </Typography>
                </Box>
                <Typography>{step.description}</Typography>
              </Grid>
            )}
          </Grid>
        ))}
        <Box
          sx={{
            marginTop: "40px",
            marginBottom: "48px",
            px: { xs: "24px", sm: "32px", md: "40px" },
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: 6,
              position: "relative",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "#1A1A1A",
                fontWeight: 700,
                fontSize: { xs: "24px", sm: "30px", md: "34px" },
                lineHeight: 1.4,
              }}
            >
              Get Fast Unsecured Business Loans with Expert Guidance
            </Typography>
          </Box>

          <Box
            sx={{
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
              border: "1px solid",
              borderColor: openContent ? "#004AAD" : "#E5E5E5",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
              boxShadow: openContent
                ? "0px 8px 30px rgba(0, 74, 173, 0.12)"
                : "0px 2px 4px rgba(0, 0, 0, 0.05)",
              transform: openContent ? "translateY(-2px)" : "none",
              "&:hover": {
                borderColor: "#004AAD",
                boxShadow: "0px 8px 30px rgba(0, 74, 173, 0.12)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box
              onClick={() => setOpenContent(!openContent)}
              sx={{
                padding: "28px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: openContent
                  ? "linear-gradient(45deg, rgba(0, 74, 173, 0.02), rgba(24, 203, 150, 0.02))"
                  : "#FFFFFF",
                borderBottom: openContent
                  ? "1px solid rgba(0, 74, 173, 0.1)"
                  : "none",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Typography
                sx={{
                  color: openContent ? "#004AAD" : "#2D3748",
                  fontWeight: 600,
                  fontSize: { xs: "16px", sm: "17px" },
                  lineHeight: 1.5,
                  transition: "all 0.3s ease",
                  flex: 1,
                  pr: 3,
                }}
              >
                How It Works – Get Fast Unsecured Business Loans with Expert
                Guidance
              </Typography>
              <Box
                sx={{
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  backgroundColor: openContent ? "#E5EEFF" : "#F8F9FA",
                  color: openContent ? "#004AAD" : "#718096",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: openContent ? "rotate(180deg)" : "rotate(0deg)",
                  "&:hover": {
                    backgroundColor: "#E5EEFF",
                    color: "#004AAD",
                  },
                }}
              >
                {openContent ? (
                  <Remove
                    sx={{
                      fontSize: "20px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                ) : (
                  <Add
                    sx={{
                      fontSize: "20px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                maxHeight: openContent ? "4000px" : "0px",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
                opacity: openContent ? 1 : 0,
                transform: openContent ? "translateY(0)" : "translateY(-12px)",
                background:
                  "linear-gradient(180deg, rgba(0, 74, 173, 0.02) 0%, rgba(24, 203, 150, 0.02) 100%)",
              }}
            >
              <Box sx={{ padding: "28px" }}>
                <Typography sx={{ color: "#4A5568", mb: 3, lineHeight: 1.8 }}>
                  We help small and growing businesses access fast, unsecured
                  business loans by connecting you with the right lender — no
                  collateral, no delays, just smart funding made simple.
                </Typography>
                <Typography sx={{ color: "#4A5568", mb: 4, lineHeight: 1.8 }}>
                  Our team of experts, business loan consultants, and small
                  business loan advisors is here to help you every step of the
                  way. We'll take time to understand your business, find the
                  right loan that matches your requirements, and help you make
                  it happen from the loan application to getting the funds
                  deposited quickly.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Loan Consultation
                    </Typography>
                    <Typography sx={{ color: "#4A5568", mb: 1 }}>
                      Your financing journey begins with a consultation from our
                      dedicated business loan consultant. Whether you are in
                      services, retail, technology, consulting, etc., we will
                      help you along the way.
                    </Typography>
                    <Typography sx={{ color: "#4A5568" }}>We:</Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Take the time to understand your business goals and
                        funding requirements.
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Make theoretical recommendations for loan options, and
                        suggest which is most suitable for your current plans.
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Provide honest and no-pressure advice when you're
                        ready to make a decision.
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Easy Application Process
                    </Typography>
                    <Typography sx={{ color: "#4A5568", mb: 1 }}>
                      No more complicated forms or long waits. We make the
                      entire loan application process smooth, fast, and easy to
                      follow.
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • There’s no collateral or security needed
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Only basic documentation, like PAN, GST returns, and
                        bank statements
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • We do a quick eligibility check, which saves your time
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Match with Trusted Lenders
                    </Typography>
                    <Typography sx={{ color: "#4A5568", mb: 1 }}>
                      Our small business loan advisors do the research so you
                      can choose confidently without wasting time. These include
                      banks, NBFCs, and other financial institutions that
                      specialize in supporting small businesses.
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Access a wide choice of lending partners
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Flexible repayment options, typically ranging from 12
                        to 60 months
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Get loan offers with competitive interest rates
                        tailored to your business
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Fast Disbursal
                    </Typography>
                    <Typography sx={{ color: "#4A5568", mb: 1 }}>
                      Once your loan is approved, we ensure the funds are
                      transferred to your account as quickly as possible, so you
                      don’t miss out on business opportunities.
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Funds are typically disbursed within 3 to 5 working
                        days
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Use the money for whatever your business needs:
                        expansion, inventory, equipment, salaries, or marketing
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Clear, transparent terms with no hidden charges
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Ongoing Support
                    </Typography>
                    <Typography sx={{ color: "#4A5568", mb: 1 }}>
                      Our support doesn’t end when you receive the loan. We stay
                      connected to help you manage your loan smoothly and grow
                      with confidence.
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Assistance with repayments, top-up loans, or
                        restructuring
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Guidance on whether you want to refinance your loan on
                        better terms later
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Reliable advice whenever you need financial planning
                        or updates
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Why Choose Us?
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Quickly secure an unsecured business loan: Leverage
                        your credit rating and the operating requirements of
                        your business, without relinquishing ownership of your
                        property.
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Service-sector expertise in Delhi. We understand the
                        unique challenges you face and can offer tailored
                        solutions that work for your industry.
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Dedicated advisors: Having worked with a wide range of
                        service-based businesses in Delhi, we work through small
                        business loan specialists and business loan consultants
                        without worry at any stage of the process.
                      </Typography>
                      <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                        • Minimal due diligence: Apply with GST returns, bank
                        statements, and basic business documents instead of an
                        audit of assets.
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004AAD", fontWeight: 700, mb: 1 }}
                    >
                      Ideal For Delhi Service Businesses
                    </Typography>
                    <Typography sx={{ color: "#4A5568", lineHeight: 1.8 }}>
                      If you're in Delhi and seeking a business loan for the
                      service sector in Delhi, whether it's for expansion,
                      working capital, equipment, or branding, our structured
                      approach suits enterprises at every stage.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            marginTop: "40px",
            marginBottom: "48px",
            px: { xs: "24px", sm: "32px", md: "40px" },
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              position: "relative",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#004AAD",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "3px",
                display: "block",
                marginBottom: "16px",
              }}
            >
              FAQs
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              maxWidth: "850px",
              margin: "0 auto",
              position: "relative",
            }}
          >
            {[
              {
                question:
                  "How does Dequitec help businesses get fast, unsecured business loans?",
                answer:
                  "We make it easy for small and growing businesses to get loans without the need for collateral. They connect you with trusted lenders who focus on business performance, not assets. The process is quick, digital, and supported by expert loan advisors to help you every step of the way.",
              },
              {
                question:
                  "What is the step-by-step process for applying for a business loan with Dequitec?",
                answer: `Talk to an advisor – They understand your business needs.\nChoose a loan – Get matched with options that fit your goals.\nApply online – Share basic documents like PAN, GST, and bank statements.\nGet lender offers – We connect you with the right lenders.\nLoan approval & disbursal – Get funds in your account within 3–5 working days.\nOngoing support – We stay with you even after the loan is disbursed.`,
              },
              {
                question:
                  "How do small business loan advisors at Dequitec guide entrepreneurs?",
                answer: `Our advisors do more than just suggest loans. They:\nListen to your needs\nAdvise the best loan options\nHelp with paperwork and the application process\nStay in touch after disbursal to help with repayments or future planning`,
              },
              {
                question:
                  "Why should I choose Dequitec as my business loan consultant?",
                answer:
                  "We make business funding easy, with no collateral, no delays, and no hidden charges. You get expert advice tailored to your needs and support at every step.",
              },
              {
                question:
                  "Can Dequitec arrange a business loan for the service sector in Delhi?",
                answer:
                  "Yes, absolutely. We helped many service-based businesses in Delhi, from salons to consultants, get the funding they need. We understand the unique needs of service-sector businesses and connect you with lenders who do too.",
              },
              {
                question:
                  "What makes Dequitec’s loan process faster than traditional banks?",
                answer:
                  "We cut out the slow steps. There’s no need for property documents or long approvals. Our digital process, expert advisors, and strong lender network help you get funding in days, not weeks.",
              },
              {
                question:
                  "Do I need collateral when applying for fast unsecured business loans through Dequitec?",
                answer:
                  "No. These are unsecured loans, which means you don’t need to put up property or assets. We help you qualify based on your business performance and financial records.",
              },
              {
                question:
                  "How does Dequitec ensure that I get the best loan options for my small business?",
                answer:
                  "We compare offers from multiple lenders based on your business type, size, and goals. Our advisors explain your options clearly, so you can pick what’s best with confidence and no pressure.",
              },
              {
                question:
                  "What industries or sectors can apply for loans through Dequitec’s platform?",
                answer: `Almost any small business can apply. We work with a wide range of sectors, including:\n\nServices (salons, consultants, freelancers)\n\nRetail and trading\n\nTech and IT services\n\nCoaching centers, healthcare, and more\n\nIf you run a business and need funding, we’re here to help.`,
              },
              {
                question:
                  "Can I track the status of my business loan application with Dequitec?",
                answer:
                  "Yes. We keep you updated at every step from application to approval and disbursal. Your dedicated advisor is always available if you have questions or need help along the way.",
              },
            ].map((faq, index) => (
              <Box
                key={index}
                sx={{
                  borderRadius: "12px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid",
                  borderColor: openIndex === index ? "#004AAD" : "#E5E5E5",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  boxShadow:
                    openIndex === index
                      ? "0px 8px 30px rgba(0, 74, 173, 0.12)"
                      : "0px 2px 4px rgba(0, 0, 0, 0.05)",
                  transform: openIndex === index ? "translateY(-2px)" : "none",
                  "&:hover": {
                    borderColor: "#004AAD",
                    boxShadow: "0px 8px 30px rgba(0, 74, 173, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                  sx={{
                    padding: "28px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor:
                      openIndex === index
                        ? "linear-gradient(45deg, rgba(0, 74, 173, 0.02), rgba(24, 203, 150, 0.02))"
                        : "#FFFFFF",
                    borderBottom:
                      openIndex === index
                        ? "1px solid rgba(0, 74, 173, 0.1)"
                        : "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      color: openIndex === index ? "#004AAD" : "#2D3748",
                      fontWeight: 600,
                      fontSize: { xs: "16px", sm: "17px" },
                      lineHeight: 1.5,
                      transition: "all 0.3s ease",
                      flex: 1,
                      pr: 3,
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      backgroundColor:
                        openIndex === index ? "#E5EEFF" : "#F8F9FA",
                      color: openIndex === index ? "#004AAD" : "#718096",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform:
                        openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                      "&:hover": {
                        backgroundColor: "#E5EEFF",
                        color: "#004AAD",
                      },
                    }}
                  >
                    {openIndex === index ? (
                      <Remove
                        sx={{
                          fontSize: "20px",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    ) : (
                      <Add
                        sx={{
                          fontSize: "20px",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    maxHeight: openIndex === index ? "2000px" : "0px",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    opacity: openIndex === index ? 1 : 0,
                    transform:
                      openIndex === index
                        ? "translateY(0)"
                        : "translateY(-12px)",
                    background:
                      "linear-gradient(180deg, rgba(0, 74, 173, 0.02) 0%, rgba(24, 203, 150, 0.02) 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      padding: "28px",
                      color: "#4A5568",
                      fontSize: { xs: "15px", sm: "16px" },
                      lineHeight: 1.8,
                      whiteSpace: "pre-line",
                      borderTop: "1px solid rgba(0, 74, 173, 0.1)",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default HowItWorks;
